import { useVirtualizer, type Virtualizer, type VirtualizerOptions } from '@tanstack/react-virtual';
import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { cn } from '../lib/utils';
export type { Virtualizer };

export interface VirtualProps<TData> extends Pick<
  VirtualizerOptions<HTMLDivElement, HTMLDivElement>,
  'overscan' | 'lanes' | 'gap'
> {
  data: TData[];
  children: (item: TData, index: number) => ReactElement | null;
  estimateSize?: number | ((index: number) => number);
  className?: string;
  style?: React.CSSProperties;
  getItemKey?: (item: TData, index: number) => string | number;
  /** Called when the list bottom sentinel enters the scroll container. */
  onBottomReached?: () => void;
  onScroll?: (
    e: React.UIEvent<HTMLDivElement>,
    instance: Virtualizer<HTMLDivElement, HTMLDivElement>,
  ) => void;
  scrollEndThreshold?: number;
  /** Scroll this index into view when it changes. */
  scrollToIndex?: number;
  /**
   * TanStack Virtual’s main `onChange`: runs whenever the virtualizer recalculates
   * (scroll, item measurement, container resize, data/count changes, etc.). It can
   * fire very often—keep this callback cheap; avoid heavy work, large state updates,
   * or logging here or the list will jank.
   */
  onChange?: (instance: Virtualizer<HTMLDivElement, HTMLDivElement>) => void;
  /** Content above the first item. It scrolls with the list. */
  header?: ReactNode;
  /** Content below the last item. It scrolls with the list. */
  footer?: ReactNode;
}

export function Virtual<TData>({
  data,
  children,
  estimateSize = 100,
  overscan = 5,
  lanes = 1,
  className = '',
  style,
  getItemKey,
  gap = 0,
  onBottomReached,
  onScroll,
  scrollEndThreshold = 100,
  scrollToIndex,
  onChange,
  header,
  footer,
}: VirtualProps<TData>) {
  const parentRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);
  const hasHeader = header != null;

  useLayoutEffect(() => {
    const element = headerRef.current;
    if (!element) {
      setScrollMargin(0);
      return;
    }
    const update = () => setScrollMargin(element.offsetHeight);
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [hasHeader]);

  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => parentRef.current,
    scrollMargin,
    estimateSize: typeof estimateSize === 'function' ? estimateSize : () => estimateSize,
    overscan,
    lanes,
    gap,
    onChange,
    getItemKey: (index) => getItemKey?.(data[index], index) ?? index,
  });
  const virtualizerRef = useRef(virtualizer);
  virtualizerRef.current = virtualizer;

  useEffect(() => {
    if (scrollToIndex == null || scrollToIndex < 0) return;
    virtualizerRef.current.scrollToIndex(scrollToIndex, {
      align: 'auto',
      behavior: 'smooth',
    });
  }, [scrollToIndex]);

  const totalSize = virtualizer.getTotalSize();
  const firedRef = useRef(false);
  const onVirtualScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const nearBottom =
        e.currentTarget.scrollTop + e.currentTarget.clientHeight >=
        scrollMargin + totalSize - scrollEndThreshold;
      if (nearBottom && !firedRef.current) {
        firedRef.current = true;
        onBottomReached?.();
      } else if (!nearBottom) {
        firedRef.current = false;
      }
      onScroll?.(e, virtualizer);
    },
    [totalSize, scrollMargin, scrollEndThreshold, onScroll, virtualizer, onBottomReached],
  );

  return (
    <div
      ref={parentRef}
      className={cn('w-full grow overflow-y-auto px-6 [scrollbar-gutter:stable]', className)}
      style={style}
      onScroll={onVirtualScroll}
    >
      {hasHeader && <div ref={headerRef}>{header}</div>}
      <div
        className="relative w-full"
        style={{
          height: `${totalSize}px`,
        }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const item = data[virtualItem.index];
          const itemKey = getItemKey ? getItemKey(item, virtualItem.index) : virtualItem.index;

          return (
            <div
              key={itemKey}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: lanes > 1 ? `${(virtualItem.lane / lanes) * 100}%` : 0,
                width: lanes > 1 ? `${100 / lanes}%` : '100%',
                transform: `translateY(${virtualItem.start - scrollMargin}px)`,
              }}
            >
              {children(item, virtualItem.index)}
            </div>
          );
        })}
      </div>
      {footer}
    </div>
  );
}
